/* eslint-disable camelcase */
import { get } from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { Card, Col, Form, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { setGlobalAlert } from '../../../../actions/offer.actions';
import Button from '../../../../shared/components/Button';
import Select from '../../../../shared/components/Select';
import Pagination from '../../../../../frontend/components/components/utils/Pagination';
import Loader from '../../../../shared/components/Loader/Loader';
import useBreakpoint from '../../../../shared/hooks/useBreakpoint';
import axios from '../../../../utils/axiosConfig';
import { ACTIONS } from '../../../../utils/constants';
import { INFLUENCERS_ENDPOINTS } from '../../../../config/api';
import './ContentApproval.scss';
import UserContentCard from './UserContentCard/UserContentCard';
import { ContentApprovalBadges, STATUS_OPTIONS } from './ContentApprovalComponents';
import DraftSidePane from './DraftSidePane';

const createMarkup = (content) => ({
  __html: content
});

/**
 *
 * @param {{
 * offer: Offer
 * }} params
 * @returns
 */
const ContentApproval = () => {
  const breakpoint = useBreakpoint();
  const offer = useSelector(store => get(store, 'offer.offer'));
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const USER_POST_DRAFT_PER_PAGE = 50;

  const [state, setState] = useState({
    deliverables: {
      total: 0,
      in_feed: 0,
      instagram_stories: 0,
    },
    drafts: {
      rejected: 0,
      accepted: 0,
      needs_review: 0,
      accepted_but: 0
    },
    filterDrafts: 'all',
    userPostDrafts: [],
    draftSidePane: {
      render: false,
      open: false,
      draft: null,
      status: null,
    },
    totalPages: 0,
    currentPage: 1,
    userPostDraftsTotalCount: 0,
    // Starts true so the "no drafts yet" prompt doesn't flash before the first load
    loading: true
  });

  const { user_offer: userOffer } = offer;

  useEffect(() => { loadUserPostDrafts(state.currentPage) }, [state.filterDrafts]);

  useEffect(() => {
    const totalDeliverables = Object.keys(offer.deliverables)
      .filter(key => offer.deliverables[key])
      .reduce((sum, key) => sum + offer.deliverables[key], 0);

    setState(prevState => ({
      ...prevState,
      deliverables: {
        total: totalDeliverables,
        in_feed: totalDeliverables - get(offer, 'deliverables.instagram_stories', 0),
        instagram_stories: get(offer, 'deliverables.instagram_stories', 0)
      },
    }));
  }, [offer.deliverables]);

  const loadUserPostDrafts = (page) => {
    setState(prevState => ({ ...prevState, loading: true }));

    axios.get(
      INFLUENCERS_ENDPOINTS.USER_POST_DRAFTS.GET({ offer_id: offer.id, filter: state.filterDrafts, page })
    ).then(({ data }) => {

      const {
        total_pages,
        user_post_drafts,
        total_count,
        rejected_count,
        accepted_count,
        needs_review_count,
        accepted_but_count,
      } = data;

      setState(prevState => ({
        ...prevState,
        loading: false,
        userPostDrafts: user_post_drafts || [],
        drafts: {
          rejected: rejected_count,
          accepted: accepted_count,
          needs_review: needs_review_count,
          accepted_but: accepted_but_count
        },
        currentPage: page,
        userPostDraftsTotalCount: total_count,
        totalPages: total_pages
      }));
    }).catch((e) => {
      console.error(e);
      setState(prevState => ({ ...prevState, loading: false }));
    });
  };

  const changePage = useCallback((page) => { loadUserPostDrafts(page); }, [state]);

  const submitDrafts = () => {
    const { user_offer: userOffer } = offer;
    axios.get(`/api/v1/influencers/offers/${offer.id}/user_offers/${userOffer.id}/submit_user_post_drafts_for_review`)
      // No success banner: refreshing the offer swaps the submit prompt for the "in review" notice
      .then(() => { dispatch(setGlobalAlert({ content: null, shouldUpdateOffer: true })); })
      .catch(error => { console.log(error); });
  };

  /**
   * @param {boolean} open If side pane is open or not
   * @param {boolean} render If side pane should render or not, this is for when the side pane is created but not opened
   */
  const toggleDraftSidePane = ({
    open,
    render = true,
    success = undefined,
    draft = null,
    status,
    action = ACTIONS.CREATE,
    error = null,
  }) => {
    document.body.style.overflow = open ? 'hidden' : 'unset';
    setState(prevState => ({
      ...prevState,
      draftSidePane: {
        render,
        open,
        draft,
        status,
        error,
      }
    }));
    if (success !== undefined) {
      let alert = null;
      let variant = 'success';

      if (success) {
        switch (action) {
          case ACTIONS.CREATE:
            break;
          case ACTIONS.DELETE:
            alert = <React.Fragment>
              <i className="icon fal fa-check-circle"></i>
              {t('influencers.pages.campaign.drafts.deleted_successfully')}
            </React.Fragment>;
            break;
          case ACTIONS.UPDATE:
            alert = <React.Fragment>
              <i className="icon fal fa-check-circle"></i>
              {t('influencers.pages.campaign.drafts.updated_successfully')}
            </React.Fragment>;
            break;
        }
        loadUserPostDrafts(state.currentPage);
      } else {
        alert = <React.Fragment>
          <i className="icon fal fa-exclamation-circle"></i>
          {error ? error : t('influencers.pages.campaign.drafts.saved_error')}
        </React.Fragment>;
        variant = 'danger';
      }
      if (alert) {
        document.querySelector('body').scrollTo({ top: 0, behavior: 'smooth' });
      }
      // Dispatched even without an alert so the offer is refreshed and any previous alert is cleared
      dispatch(setGlobalAlert({
        content: alert,
        variant,
        shouldUpdateOffer: true
      }));
    }
  };

  /**
   * Only starts the close animation. The draft is kept so the pane doesn't flash empty while sliding out;
   * it is cleared (and the pane unmounted) in onAfterClose.
   */
  const closeDraftSidePane = () => {
    document.body.style.overflow = 'unset';
    setState(prevState => ({
      ...prevState,
      draftSidePane: { ...prevState.draftSidePane, open: false }
    }));
  };

  const onChangeFilterDrafts = useCallback(({ value }) => {
    setState(prevState => ({
      ...prevState,
      filterDrafts: value,
      currentPage: 1,
    }));
  }, []);

  const showDraftSubmit = (
    !get(userOffer, 'user_post_drafts_submitted_at') || get(userOffer, 'user_post_drafts_rejected_at')
  ) && get(userOffer, 'user_post_drafts_attributes_valid') && state.userPostDraftsTotalCount > 0;

  const draftsInReview = !!get(userOffer, 'user_post_drafts_submitted_at') &&
    !get(userOffer, 'user_post_drafts_rejected_at') && state.userPostDraftsTotalCount > 0;

  // Shown even when drafts are incomplete, since that is when the influencer needs the edit guidance
  const contentWorkNeeded = !!get(userOffer, 'user_post_drafts_rejected_at') && state.userPostDraftsTotalCount > 0;

  const noDraftsYet = !state.loading && state.userPostDraftsTotalCount === 0 &&
    !get(userOffer, 'user_post_drafts_submitted_at');

  // Every draft is Approved or Conditionally Approved and the campaign's required number of drafts is met
  const allDraftsApproved = state.userPostDraftsTotalCount > 0 &&
    state.drafts.accepted + state.drafts.accepted_but === state.userPostDraftsTotalCount &&
    state.userPostDraftsTotalCount >= get(offer, 'content_approval_drafts_count', 0);

  // Shown as soon as one draft exists; the Submit link itself waits for showDraftSubmit
  const draftsNotSubmitted = state.userPostDraftsTotalCount > 0 && !get(userOffer, 'user_post_drafts_submitted_at');

  return (<div className='mb-6'>
    {
      breakpoint === 'xs' ?
        <h4 className='mb-3'>
          {t('influencers.pages.campaign.content_approval')}
        </h4> :
        <h3 className='mb-3'>
          {t('influencers.pages.campaign.content_approval')}
        </h3>
    }
    <div className='mb-4'
      dangerouslySetInnerHTML={createMarkup(get(offer, 'content_approval_influencer_instructions', ''))} />
    <Row className='mb-6'>
      <Col xs={12} sm={6} className="mb-3 mb-sm-0">
        <div className="content-approval-summary">
          <div className="text-2 mb-2">
            {t('influencers.pages.campaign.asking_for')}
          </div>
          <div className='text-medium-1'>
            {[
              ...Object.keys(offer.deliverables)
                .filter(key => offer.deliverables[key] && key !== 'other')
                .map(key => `${offer.deliverables[key]} ${t(`influencers.common.posts.${key}`, {
                  count: offer.deliverables[key]
                })}`),
              ...(get(offer, 'deliverables.other') ?
                [`${offer.deliverables.other} ${offer.other_deliverable_label}`] : [])
            ].join(', ')}
          </div>
        </div>
      </Col>
      <Col xs={12} sm={6}>
        <div className="content-approval-summary">
          <div className="text-2 mb-2">
            {t('influencers.pages.campaign.drafts_submitted')}
          </div>
          <div className='text-medium-1'>
            {t('influencers.pages.campaign.number_of_total_drafts', {
              of: state.userPostDraftsTotalCount,
              total: get(offer, 'content_approval_drafts_count', 0)
            })}
          </div>
        </div>
      </Col>
    </Row>
    <Row className='align-items-center mb-3'>
      <Col xs={12} lg={8}>
        {
          breakpoint === 'xs' ?
            <h4 className='mb-3 mb-lg-0'>
              {t('influencers.pages.campaign.all_drafts')}
            </h4> :
            <h3 className='mb-3 mb-lg-0'>
              {t('influencers.pages.campaign.all_drafts')}
            </h3>
        }
      </Col>
      {state.userPostDraftsTotalCount > 0 &&
        <Col xs={12} lg={4}>
          <Form.Group className='mb-0'>
            <Form.Label>
              {t('influencers.content_approval.view_filter')}
            </Form.Label>
            <Select className="mb-2"
              placeholder={t('influencers.content_approval.all_drafts')}
              onChange={onChangeFilterDrafts}
              options={[
                { label: t('influencers.content_approval.all_drafts'), value: 'all' },
                ...STATUS_OPTIONS.map(status => (
                  {
                    label: t(`common.content_approval.status.${status}`),
                    value: status
                  }
                ))
              ]} />
            <Form.Text className="text-muted text-right">
              {t('influencers.content_approval.showing_n_drafts', {
                n: state.userPostDrafts.length
              })}
            </Form.Text>
          </Form.Group>
        </Col>
      }
    </Row>
    {state.userPostDraftsTotalCount > 0 &&
      <Row>
        <ContentApprovalBadges statusDrafts={state.drafts} />
      </Row>
    }
    <hr />

    {(noDraftsYet || (draftsNotSubmitted && !contentWorkNeeded)) &&
      <div className="submit-for-review-prompt mt-4 mb-4">
        <div className="d-flex align-items-start">
          <i className="fal fa-info-circle"></i>
          <div className="text-1">
            {noDraftsYet ?
              t('influencers.content_approval.get_started_prompt') :
              t('influencers.content_approval.submit_for_review_prompt')}
          </div>
        </div>
        {showDraftSubmit &&
          <div className="d-flex justify-content-end mt-3">
            <Button className="p-0" variant="tertiary" onClick={submitDrafts}>
              {t('influencers.content_approval.submit_for_review')}
              <i className="fal fa-arrow-right icon-right"></i>
            </Button>
          </div>
        }
      </div>
    }
    {(draftsInReview || contentWorkNeeded) &&
      <div className="submitted-for-review-notice mt-4 mb-4">
        <div className="d-flex align-items-start">
          <i className="fal fa-info-circle"></i>
          {contentWorkNeeded ?
            <div>
              <div className="text-medium-2 mb-1">
                {t('influencers.content_approval.content_work_needed_title')}
              </div>
              <div className="text-2">
                {t('influencers.content_approval.content_work_needed_prompt')}
              </div>
            </div> :
            <div className="text-2">
              {t('influencers.content_approval.submitted_for_review_notice')}
            </div>
          }
        </div>
        {contentWorkNeeded && showDraftSubmit &&
          <div className="d-flex justify-content-end mt-3">
            <Button className="p-0" variant="tertiary" onClick={submitDrafts}>
              {t('influencers.content_approval.submit_for_review')}
              <i className="fal fa-arrow-right icon-right"></i>
            </Button>
          </div>
        }
      </div>
    }

    <Row className="drafts-grid mt-3 mt-sm-1">
      {state.loading ?
        <Loader loading={state.loading} /> :
        <React.Fragment>
          {
            state.userPostDrafts.map(draft =>
              <Col xs={12} sm={6} md={6} key={draft.id} className="d-flex flex-column">
                <UserContentCard
                  userContent={draft}
                  userContentType='userPostDraft'
                  onToggle={(status = null) => toggleDraftSidePane({
                    open: true,
                    draft,
                    status,
                  })}
                  offerStatus={get(offer, 'user_offer.status')}
                  showDraftSubmit={showDraftSubmit}
                />
              </Col>
            )
          }
          {!allDraftsApproved && <Col xs={12} sm={6} md={6} className="d-flex flex-column">
            <Card as="button" type="button" className="add-draft-card mb-2" data-cy='Add Draft'
              onClick={() => toggleDraftSidePane({ open: true, render: true })}>
              <div className="add-draft-icon mb-3">
                <i className="fal fa-plus"></i>
              </div>
              <div className="text-medium-1 mb-2">
                {t('influencers.pages.campaign.add_draft')}
              </div>
              <div className="text-2 add-draft-description">
                {t('influencers.pages.campaign.add_draft_description')}
              </div>
            </Card>
          </Col>}
          {state.userPostDraftsTotalCount > USER_POST_DRAFT_PER_PAGE && <Col xs="12" className="text-center mt-4">
            <Pagination
              currentPage={state.currentPage}
              lastPage={state.totalPages}
              fetchData={changePage}
            />
          </Col>}
        </React.Fragment>
      }
    </Row>

    {state.draftSidePane.render && <DraftSidePane
      open={state.draftSidePane.open}
      draft={state.draftSidePane.draft}
      status={state.draftSidePane.status}
      confirmNewContent={!!get(userOffer, 'user_post_drafts_submitted_at')}
      onRequestClose={closeDraftSidePane}
      onAfterClose={(success, action = ACTIONS.CREATE, error = null) =>
        toggleDraftSidePane({ open: false, render: false, success, action, error })
      }
    />}

  </div >);
};

ContentApproval.propTypes = {
  offer: PropTypes.any.isRequired
};

ContentApproval.defaultProps = {
};

export default ContentApproval;
